// store/documentSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AnalysisResults } from '../types';
import { config } from '../utils/config';
import { apiClient } from '../api/client';

interface DocumentState {
 documents: AnalysisResults[];
 currentDocument: AnalysisResults | null;
 loading: boolean;
 error: string | null;
}

const initialState: DocumentState = {
 documents: [],
 currentDocument: null,
 loading: false,
 error: null,
};

// Async thunks for API calls
export const fetchDocuments = createAsyncThunk(
 'documents/fetchAll',
 async () => {
   const response = await apiClient.get(config.endpoints.documents);
   return response.data;
 }
);

export const fetchDocumentById = createAsyncThunk(
 'documents/fetchById',
 async (id: number) => {
   const response = await apiClient.get(`${config.endpoints.documents}/${id}`);
   return response.data;
 }
);

export const updateDocumentStatus = createAsyncThunk(
 'documents/updateStatus',
 async ({ id, invoice_status, invoice_direction }: { id: number; invoice_status?: string | null; invoice_direction?: string | null }) => {
   const response = await apiClient.patch(`${config.endpoints.documents}/${id}/status`, {
     invoice_status,
     invoice_direction,
   });
   return { id, ...response.data };
 }
);

const documentSlice = createSlice({
 name: 'documents',
 initialState,
 reducers: {
   addDocument: (state, action: PayloadAction<AnalysisResults>) => {
     // Add document to list if not already present
     const existingIndex = state.documents.findIndex(doc => doc.metadata.filename === action.payload.metadata.filename);
     if (existingIndex >= 0) {
       state.documents[existingIndex] = action.payload;
     } else {
       state.documents.unshift(action.payload);
     }
   },
   removeDocument: (state, action: PayloadAction<number>) => {
     // Remove document from list by ID
     state.documents = state.documents.filter(doc => doc.id !== action.payload);
     // Clear current document if it's the one being removed
     if (state.currentDocument?.id === action.payload) {
       state.currentDocument = null;
     }
   },
   setCurrentDocument: (state, action: PayloadAction<AnalysisResults | null>) => {
     state.currentDocument = action.payload;
   },
   setLoading: (state, action: PayloadAction<boolean>) => {
     state.loading = action.payload;
   },
   setError: (state, action: PayloadAction<string | null>) => {
     state.error = action.payload;
   },
   clearError: (state) => {
     state.error = null;
   }
 },
 extraReducers: (builder) => {
   builder
     // Handle fetchDocuments
     .addCase(fetchDocuments.pending, (state) => {
       state.loading = true;
       state.error = null;
     })
     .addCase(fetchDocuments.fulfilled, (state, action) => {
       state.loading = false;
       state.documents = action.payload;
     })
     .addCase(fetchDocuments.rejected, (state, action) => {
       state.loading = false;
       state.error = action.error.message || 'Failed to fetch documents';
     })
     // Handle fetchDocumentById
     .addCase(fetchDocumentById.pending, (state) => {
       state.loading = true;
       state.error = null;
     })
     .addCase(fetchDocumentById.fulfilled, (state, action) => {
       state.loading = false;
       state.currentDocument = action.payload;
     })
     .addCase(fetchDocumentById.rejected, (state, action) => {
       state.loading = false;
       state.error = action.error.message || 'Failed to fetch document';
     })
     // Handle updateDocumentStatus
     .addCase(updateDocumentStatus.fulfilled, (state, action) => {
       const { id, invoice_status, invoice_direction } = action.payload;
       const doc = state.documents.find(d => d.id === id);
       if (doc) {
         doc.invoice_status = invoice_status;
         doc.invoice_direction = invoice_direction;
       }
     });
 }
});

export const { addDocument, removeDocument, setCurrentDocument, setLoading, setError, clearError } = documentSlice.actions;
export default documentSlice.reducer;